import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Grid,
  FormInput,
  FormSelectV2,
  Checkbox,
  toast,
  Spinner,
} from '@lionstep/ui';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

// Constants
import { JOB_LANGUAGE_OPTIONS } from '../../../../../../job/job.constants';
import { USER_ROLES } from '../../../../../../../constants/user.const';

// Helpers
import {
  constantsArrayWithTranslatedLabel,
  langShorthand,
} from '../../../../../../../support';

// Queries
import {
  useUserCollaborators,
  useCreateUserCollaboratorMutation,
} from '../../../../../../../queries/userQueries';
import {
  useAddUserToJobMutation,
  useAddJobMailingPermissionsMutation,
  useSendUserNewJobEmailMutation,
} from '../../../../../../../queries/jobQueries';

// Styles
import styles from './addNewCollaborator.module.scss';

const DEFAULT_FORM_VALUES = {
  name: '',
  email: '',
  language: { value: 'en', label: 'English' },
};

const AddNewCollaborator = ({ job }) => {
  const { t } = useTranslation();

  const schema = Yup.object().shape({
    email: Yup.string().email().required(t('Required')),
    name: Yup.string()
      // https://stackoverflow.com/a/57754227
      .matches(
        // TODO: move RegExp to some constants file
        /[A-Za-zÀ-ž\u0370-\u03FF\u0400-\u04FF]+\s+[A-Za-zÀ-ž\u0370-\u03FF\u0400-\u04FF]+/,
        t('Please provide your full name'),
      )
      .max(140)
      .required(t('Required')),
    language: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required(t('Required')),
  });

  // State
  const [shouldAddMailingPermission, setShouldAddMailingPermission] =
    useState(true);

  // Queries

  // Get collaborators
  const { refetch: getUserCollaborators } = useUserCollaborators({
    options: { enabled: false },
  });

  // Create Collaborator
  const {
    mutateAsync: createUserCollaborator,
    isLoading: isLoadingCreateUserCollaborator,
  } = useCreateUserCollaboratorMutation({
    onError: (error) => {
      toast.error('There was an error while adding collaborator.', {
        id: 'CREATE_USER_COLLABORATOR',
        statusCode: error?.response?.status,
      });
    },
  });

  // Add User to Job
  const { mutateAsync: addUserToJob, isLoading: isLoadingAddUserToJob } =
    useAddUserToJobMutation({
      onError: (error) => {
        toast.error('There was an error while adding job user.', {
          id: 'POST_ADD_JOB_USER',
          statusCode: error?.response?.status,
        });
      },
    });

  // Add Job Mailing Permission
  const {
    mutateAsync: addJobMailingPermission,
    isLoading: isLoadingAddJobMailingPermission,
  } = useAddJobMailingPermissionsMutation({
    onError: (error) => {
      toast.error('There was an error while adding job mailing permission.', {
        id: 'POST_ADD_JOB_MAILING_PERMISSION',
        statusCode: error?.response?.status,
      });
    },
  });

  // Send User New Job Email
  const {
    mutateAsync: sendUserNewJobEmail,
    isLoading: isLoadingSndUserNewJobEmail,
  } = useSendUserNewJobEmailMutation({});

  // options that needs translating
  const JOB_LANGUAGE_OPTIONS_TRANSLATED = constantsArrayWithTranslatedLabel(
    t,
    JOB_LANGUAGE_OPTIONS,
  );

  const formMethods = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods;

  const handleOnSubmit = async (data) => {
    const [firstName, lastName] = data.name.split(' ');

    const newCollaborator = await createUserCollaborator({
      first_name: firstName,
      last_name: lastName,
      email: data.email,
      role: USER_ROLES.viewer,
      language: langShorthand(data.language.value),
    });

    await addUserToJob({
      jobId: job.id,
      userId: newCollaborator.local_id,
    });

    if (shouldAddMailingPermission) {
      await addJobMailingPermission({
        jobId: job.id,
        data: { user_id: newCollaborator.local_id },
      });
    }

    await sendUserNewJobEmail({
      user: newCollaborator.user,
      jobTitle: job.opening_title,
    });

    getUserCollaborators();
    reset(DEFAULT_FORM_VALUES);
  };

  const isLoading =
    isLoadingCreateUserCollaborator ||
    isLoadingAddJobMailingPermission ||
    isLoadingSndUserNewJobEmail ||
    isLoadingAddUserToJob;

  return (
    <FormProvider {...formMethods}>
      {isLoading && <Spinner position="absolute" size={100} />}
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <Grid.Row gutter={10} className={styles.addNewViewerForm}>
          <Grid.Col xs={24} md={12} lg={8}>
            <FormInput
              formItemClassName={styles.formItems}
              label={t('add_new_viewer')}
              name="name"
              placeholder="John Doe"
              error={errors.name?.message}
            />
          </Grid.Col>
          <Grid.Col xs={24} md={12} lg={8}>
            <FormInput
              formItemClassName={styles.formItems}
              label={t('work_email')}
              name="email"
              placeholder="email@example.com"
              error={errors.email?.message}
            />
          </Grid.Col>
          <Grid.Col xs={24} md={12} lg={4}>
            <FormSelectV2
              formItemClassName={styles.formItems}
              label={t('email_lang')}
              name="language"
              error={errors.language?.message}
              options={JOB_LANGUAGE_OPTIONS_TRANSLATED}
            />
          </Grid.Col>
          <Grid.Col xs={24} md={12} lg={4}>
            <Button
              disabled={isLoading}
              typeHtml="submit"
              block
              className={styles.submitButton}
            >
              {t('invite')}
            </Button>
          </Grid.Col>
        </Grid.Row>
        <Grid.Row className={styles.sendCollaboratorStatus}>
          <Grid.Col xs={24}>
            <Checkbox
              inputAlign
              name="userJobNotifications"
              checked={shouldAddMailingPermission}
              label={t('send_email_updates')}
              onChange={(e) => setShouldAddMailingPermission(e.target.checked)}
            />
          </Grid.Col>
        </Grid.Row>
      </form>
    </FormProvider>
  );
};

AddNewCollaborator.defaultProps = {
  job: {},
};

AddNewCollaborator.propTypes = {
  job: PropTypes.shape(),
};

export default AddNewCollaborator;
